/* Filling an Office template without destroying what makes it a template.
 *
 * The rule throughout: never write formatting, only text. Every font, colour,
 * indent and table style in the WAPA deck was decided by somebody who knows
 * what the client expects to see, and none of it is ours to re-derive. So the
 * XML is edited in place, the smallest edit that will do.
 *
 * The trap that makes naive replacement fail: PowerPoint splits a paragraph
 * into runs wherever anything changes, including things a person cannot see
 * such as a spell-check boundary or a stray revision. "[Month Year]" is
 * routinely stored as "[Month" in one run and " Year]" in the next. Search a
 * run at a time and the placeholder is never found, silently, on some slides
 * and not others.
 *
 * So everything here works a paragraph at a time: join the runs, do the work
 * on the joined text, put the result back in the first run and empty the rest.
 * The first run keeps its formatting, which is the formatting the template
 * author chose for that line.
 */

export const escapeXml = (s) => String(s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;").replace(/'/g, "&apos;")
  .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, "");

export const unescapeXml = (s) => String(s)
  .replace(/&lt;/g, "<").replace(/&gt;/g, ">")
  .replace(/&quot;/g, '"').replace(/&apos;/g, "'")
  .replace(/&amp;/g, "&");

const PARA = /<a:p(?:\s[^>]*)?>[\s\S]*?<\/a:p>|<a:p\s*\/>/g;
const RUN = /<a:r(?:\s[^>]*)?>[\s\S]*?<\/a:r>/g;
const TEXT = /(<a:t(?:\s[^>]*)?>)([\s\S]*?)(<\/a:t>)/;

/* A run's visible text, unescaped. */
function runText(run) {
  const m = TEXT.exec(run);
  return m ? unescapeXml(m[2]) : "";
}

function withText(run, text) {
  return run.replace(TEXT, (_, open, __, close) => {
    /* Leading or trailing spaces are dropped by every reader unless the
       element says to keep them, and a table cell reading "102 days" that
       arrives as "102days" is the kind of defect nobody spots in review.
       Added only where the text actually needs it, so filling a template
       changes as little of its XML as possible. */
    const needed = /^\s|\s$/.test(text);
    const tag = (!needed || /xml:space=/.test(open))
      ? open
      : open.replace(/>$/, ' xml:space="preserve">');
    return tag + escapeXml(text) + close;
  });
}

/**
 * Rewrites every paragraph's text through `change`, which is handed the whole
 * paragraph's text and returns what it should say. Returning the same string
 * leaves the paragraph untouched, bit for bit.
 */
export function mapParagraphs(xml, change) {
  return xml.replace(PARA, (para) => {
    const runs = para.match(RUN);
    if (!runs?.length) return para;

    const before = runs.map(runText).join("");
    const after = change(before);
    if (after === before) return para;

    let first = true;
    return para.replace(RUN, (run) => {
      if (first) { first = false; return withText(run, after); }
      /* Emptied rather than removed: a run can carry a hyperlink or a language
         setting that the paragraph's later runs inherit from, and removing it
         changes the paragraph in ways that only show up in PowerPoint. */
      return withText(run, "");
    });
  });
}

/* A template placeholder: [Month Year], [##%], [# / #], [Status]. */
export const PLACEHOLDER = /\[[^\][\n]{0,120}\]/g;

/**
 * Replaces placeholders by exact text, e.g. { "[Month Year]": "July 2026" }.
 * A placeholder with no entry is left exactly as it was, so that what is
 * missing stays visible rather than turning into a blank nobody notices.
 *
 * A list fills the placeholder's occurrences in reading order, one value
 * each. The WAPA schedule slide says [Mon YYYY] fourteen times and means
 * fourteen different months, so one value for all of them would be wrong on
 * thirteen. Occurrences past the end of the list stay as they were, and so
 * does any whose value is the placeholder itself, which is how a planner
 * says "this one I cannot fill" without losing its place in the order.
 */
export function fillPlaceholders(xml, values) {
  const has = Object.prototype.hasOwnProperty;
  const seen = new Map();
  return mapParagraphs(xml, (text) =>
    text.replace(PLACEHOLDER, (found) => {
      if (!has.call(values, found)) return found;
      const value = values[found];
      if (!Array.isArray(value)) return String(value ?? "");
      const at = seen.get(found) ?? 0;
      seen.set(found, at + 1);
      return at < value.length ? String(value[at] ?? "") : found;
    }));
}

/**
 * Rewrites whole paragraphs chosen by how they begin: `rules` is a list of
 * { startsWith, text }, and a text of null removes the paragraph. For a line
 * whose wording depends on the numbers, such as an URGENT banner with nothing
 * to be urgent about this month, or a footnote about one vendor's terms when
 * no vendor has unusual terms.
 */
export function rewriteParagraphs(xml, rules = []) {
  if (!rules.length) return xml;
  return xml.replace(PARA, (para) => {
    const runs = para.match(RUN);
    if (!runs?.length) return para;
    const text = runs.map(runText).join("");
    const rule = rules.find((r) => text.startsWith(r.startsWith));
    if (!rule) return para;
    if (rule.text === null) return "";
    return mapParagraphs(para, () => rule.text);
  });
}

function paragraphTexts(xml) {
  const out = [];
  mapParagraphs(xml, (t) => { out.push(t); return t; });
  return out;
}

const BODY = /<p:txBody>[\s\S]*?<\/p:txBody>/g;

/**
 * Fills a text box that holds a list, one paragraph per line. The box is
 * found by a placeholder only it contains, its first paragraph with words in
 * it is the pattern, and the template's example lines are replaced rather
 * than added to, so five example milestones become however many there are.
 * An empty list leaves the box alone, placeholders and all, so it is
 * reported rather than quietly blanked.
 */
export function fillList(xml, anchor, lines = []) {
  if (!lines.length) return xml;
  let done = false;
  return xml.replace(BODY, (body) => {
    if (done || !paragraphTexts(body).some((t) => t.includes(anchor))) return body;
    const paras = body.match(PARA) ?? [];
    const pattern = paras.find((p) => HAS_RUN.test(p));
    if (!pattern) return body;
    done = true;
    const start = body.indexOf(paras[0]);
    const last = paras[paras.length - 1];
    const end = body.lastIndexOf(last) + last.length;
    const filled = lines.map((line) => mapParagraphs(pattern, () => String(line))).join("");
    return body.slice(0, start) + filled + body.slice(end);
  });
}

/** Every placeholder still present, in order, deduplicated. */
export function remainingPlaceholders(xml) {
  const found = [];
  const seen = new Set();
  mapParagraphs(xml, (text) => {
    for (const p of text.match(PLACEHOLDER) ?? []) {
      if (!seen.has(p)) { seen.add(p); found.push(p); }
    }
    return text;
  });
  return found;
}

/* ------------------------------------------------------------------ tables */

const TABLE = /<a:tbl>[\s\S]*?<\/a:tbl>/g;
const ROW = /<a:tr(?:\s[^>]*)?>[\s\S]*?<\/a:tr>/g;
const CELL = /<a:tc(?:\s[^>]*)?>[\s\S]*?<\/a:tc>|<a:tc(?:\s[^>]*)?\/>/g;

export function tableRows(tbl) { return tbl.match(ROW) ?? []; }
export function rowCells(row) { return row.match(CELL) ?? []; }

/* The text of each cell in a row, for reading a template back. */
export function rowText(row) {
  return rowCells(row).map((cell) => {
    const parts = [];
    mapParagraphs(cell, (t) => { parts.push(t); return t; });
    return parts.join(" ").trim();
  });
}

const HAS_RUN = /<a:r(?:\s|>)/;

/* A run for a paragraph that has none, dressed in the paragraph's own
   end-of-paragraph properties. That element is where PowerPoint keeps what
   the next thing typed would look like, so it is the formatting the template
   author chose for a cell that was left empty. It is the same kind of element
   as a run's properties, so it converts by name, children and all, and the
   new run goes in front of it because the file format requires runs first. */
function seedRun(para, value) {
  const run = (props) => withText(`<a:r>${props}<a:t></a:t></a:r>`, value);

  if (/^<a:p\s*\/>$/.test(para)) return `<a:p>${run("")}</a:p>`;

  const end = /<a:endParaRPr(\s[^>]*?)?(?:\/>|>([\s\S]*?)<\/a:endParaRPr>)/.exec(para);
  if (end) {
    const attrs = end[1] ?? "";
    const props = end[2] === undefined ? `<a:rPr${attrs}/>` : `<a:rPr${attrs}>${end[2]}</a:rPr>`;
    return para.slice(0, end.index) + run(props) + para.slice(end.index);
  }
  return para.replace(/<\/a:p>$/, `${run("")}</a:p>`);
}

/** Writes one cell's text. Only the first paragraph with words in it carries
 *  the value; later ones go, because a template cell with two example lines
 *  would otherwise keep the second. A cell with no words in it at all takes
 *  the value in its first paragraph, rather than quietly dropping it. */
function writeCell(cell, value) {
  /* A value with line breaks in it becomes one paragraph per line, each a
     copy of the first, so "Analysis (2): ..." and "Testing (7): ..." sit on
     lines of their own in the template's own type. */
  const lines = String(value ?? "").split("\n");
  const text = lines[0];
  let first = true;
  const out = cell.replace(PARA, (para) => {
    if (!HAS_RUN.test(para)) return para;
    if (first) {
      first = false;
      return lines.map((line) => mapParagraphs(para, () => line)).join("");
    }
    return "";
  });
  if (!first || text === "") return out;

  /* A cell with no text body at all. Rare, but the value still has to land. */
  if (!PARA.test(out)) {
    PARA.lastIndex = 0;
    const body = `<a:txBody><a:bodyPr/><a:lstStyle/>${seedRun("<a:p/>", text)}</a:txBody>`;
    return /\/>$/.test(out)
      ? out.replace(/\s*\/>$/, `>${body}</a:tc>`)
      : out.replace(/^(<a:tc(?:\s[^>]*)?>)/, `$1${body}`);
  }
  PARA.lastIndex = 0;

  let seeded = false;
  return out.replace(PARA, (para) => {
    if (seeded) return para;
    seeded = true;
    return seedRun(para, text);
  });
}

/* ----------------------------------------------------------------- colour */

const HEX = /^[0-9A-Fa-f]{6}$/;
const FILL = /<a:noFill\/>|<a:solidFill>[\s\S]*?<\/a:solidFill>|<a:solidFill\/>|<a:gradFill\b[\s\S]*?<\/a:gradFill>|<a:pattFill\b[\s\S]*?<\/a:pattFill>|<a:blipFill\b[\s\S]*?<\/a:blipFill>|<a:grpFill\/>/y;

const solid = (hex) => {
  if (!HEX.test(String(hex))) {
    throw new Error(`"${hex}" is not a six digit colour. A bad colour would make a file PowerPoint refuses.`);
  }
  return `<a:solidFill><a:srgbClr val="${String(hex).toUpperCase()}"/></a:solidFill>`;
};

/* Walks past the elements that the file format puts in front of a fill, and
   returns where the fill belongs. In a cell's properties those are the six
   border lines and any 3D setting; in a run's properties, its outline. Getting
   this wrong is what repaints a border instead of the cell. */
function afterLeading(inner, tags) {
  const open = new RegExp(`<a:(${tags})\\b[^>]*?(/>|>)`, "y");
  let at = 0;
  for (;;) {
    while (/\s/.test(inner[at] ?? "")) at += 1;
    open.lastIndex = at;
    const m = open.exec(inner);
    if (!m) return at;
    if (m[2] === "/>") { at = open.lastIndex; continue; }
    const close = inner.indexOf(`</a:${m[1]}>`, open.lastIndex);
    if (close === -1) return at;
    at = close + `</a:${m[1]}>`.length;
  }
}

/* Sets the fill inside a properties element's content: replaces the one that
   is there, of whatever kind, or puts one where the format expects it. */
function setFill(inner, tags, hex) {
  const at = afterLeading(inner, tags);
  FILL.lastIndex = at;
  const m = FILL.exec(inner);
  const end = m ? FILL.lastIndex : at;
  return inner.slice(0, at) + solid(hex) + inner.slice(end);
}

const CELL_LEAD = "lnL|lnR|lnT|lnB|lnTlToBr|lnBlToTr|cell3D";
const TEXT_LEAD = "ln";

/**
 * Colours a table cell: `fill` is its background, `ink` its text, each a six
 * digit hex such as "C6EFCE". Both are wanted together for a status cell: the
 * deck's green box is a green fill with dark green words in it, which is
 * Excel's own Good style, because that table began life in Excel.
 *
 * Ink is set on every run and on the end-of-paragraph properties, so a cell
 * written into later, or one with no words yet, comes out the same colour.
 */
export function paintCell(cell, { fill, ink } = {}) {
  let out = cell;
  if (fill) {
    out = out.replace(/<a:tcPr(\s[^>]*?)?(?:\/>|>([\s\S]*?)<\/a:tcPr>)/, (_, attrs = "", inner) =>
      `<a:tcPr${attrs}>${setFill(inner ?? "", CELL_LEAD, fill)}</a:tcPr>`);
    /* No properties element yet. It belongs straight after the text body. */
    if (!/<a:tcPr[\s>/]/.test(out)) {
      const props = `<a:tcPr>${solid(fill)}</a:tcPr>`;
      out = /<\/a:txBody>/.test(out)
        ? out.replace(/<\/a:txBody>/, `</a:txBody>${props}`)
        : out.replace(/(<\/a:tc>)$/, `${props}$1`);
    }
  }
  if (ink) {
    out = out.replace(/<a:(rPr|endParaRPr)(\s[^>]*?)?(?:\/>|>([\s\S]*?)<\/a:\1>)/g,
      (_, tag, attrs = "", inner) => `<a:${tag}${attrs}>${setFill(inner ?? "", TEXT_LEAD, ink)}</a:${tag}>`);
  }
  return out;
}

/* A cell to leave exactly as the template has it: a label the template
   author wrote, or a placeholder that is a person's to fill and should stay
   visible as one. */
export const KEEP = Symbol("keep the template's own cell");

/** Writes one row's cells. Extra values are ignored; missing ones empty the
 *  cell, because a template row carries example text that must not survive.
 *  A value is text, KEEP, or { text, fill, ink } for a cell that also takes a
 *  colour. */
function writeRow(row, values) {
  let at = -1;
  return row.replace(CELL, (cell) => {
    at += 1;
    const value = at < values.length ? values[at] : "";
    if (value === KEEP) return cell;
    const painted = value !== null && typeof value === "object";
    /* Words first, then colour, so a run the words had to create is painted
       along with the rest. */
    const out = writeCell(cell, painted ? value.text : value);
    return painted ? paintCell(out, value) : out;
  });
}

/**
 * Fills a table from `rows`, a list of cell-value lists.
 *
 * The body rows of the template are patterns. The first is cloned as often as
 * needed, so a table built for five vendors takes twelve without anybody
 * editing the template, and shrinks to two without leaving empty rows behind.
 *
 * Cloning the first body row rather than cycling through all of them is
 * deliberate: banded tables alternate fill by row, and reusing row two for
 * every other line produces stripes that stop lining up the moment the row
 * count changes.
 */
export function fillTable(tbl, rows, { headerRows = 1 } = {}) {
  const all = tableRows(tbl);
  if (all.length <= headerRows) return tbl;

  const head = all.slice(0, headerRows);
  const patterns = all.slice(headerRows);
  /* A row can name the template row it is dressed as: { cells, like }, where
     like counts body rows from zero. The deliverables tracker has a grey
     subtotal row and bold total rows under its vendors, and a total written
     in a vendor row's clothes reads as one more vendor. */
  const body = rows.map((values) => (Array.isArray(values)
    ? writeRow(patterns[0], values)
    : writeRow(patterns[Math.min(values.like ?? 0, patterns.length - 1)], values.cells ?? [])));

  const rebuilt = head.join("") + body.join("");
  const start = tbl.indexOf(all[0]);
  const lastRow = all[all.length - 1];
  const end = tbl.lastIndexOf(lastRow) + lastRow.length;
  return tbl.slice(0, start) + rebuilt + tbl.slice(end);
}

/** Applies `fill` to the nth table on a slide, counting from zero. */
export function mapTable(xml, index, fill) {
  let at = -1;
  return xml.replace(TABLE, (tbl) => {
    at += 1;
    return at === index ? fill(tbl) : tbl;
  });
}

export function tablesOf(xml) {
  return xml.match(TABLE) ?? [];
}

export function countTables(xml) {
  return (xml.match(TABLE) ?? []).length;
}
